<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <!-- 页面头部 -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-4">
        <button @click="handleBack"
          class="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
          <ArrowLeftIcon class="w-6 h-6" />
        </button>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {{ isEditMode ? '编辑串题组' : '新建串题组' }}
        </h1>
      </div>
      <!-- 新建模式显示保存/取消按钮 -->
      <div v-if="!isEditMode" class="flex gap-2">
        <button @click="handleBack"
          class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
          取消
        </button>
        <button @click="handleSave" :disabled="saving"
          class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {{ saving ? '保存中...' : '创建' }}
        </button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 编辑内容 -->
    <div v-else class="space-y-6">
      <!-- 基本信息 -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">基本信息</h2>
          <!-- 编辑模式显示保存按钮 -->
          <button v-if="isEditMode" @click="handleSaveBasicInfo" :disabled="saving || !isBasicInfoDirty"
            class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm">
            {{ saving ? '保存中...' : '保存基本信息' }}
          </button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- 组名 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              组名 <span class="text-red-500">*</span>
            </label>
            <input v-model="form.name" type="text" placeholder="如：人物-老师/导师类"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
          </div>
          <!-- 分类 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              分类 <span class="text-red-500">*</span>
            </label>
            <select v-model="form.categoryId"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
              <option :value="undefined" disabled>请选择分类</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>
          <!-- 排序 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
            <input v-model.number="form.orderId" type="number" placeholder="数字越小越靠前"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
          </div>
          <!-- 状态 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">状态</label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input v-model="form.isVisible" type="checkbox" class="w-4 h-4 text-indigo-600 rounded" />
              <span class="text-gray-700 dark:text-gray-300">显示</span>
            </label>
          </div>
          <!-- 权限控制 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">权限控制</label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input v-model="form.isFree" type="checkbox" class="w-4 h-4 text-indigo-600 rounded" />
              <span class="text-gray-700 dark:text-gray-300">完全免费</span>
            </label>
          </div>
          <!-- 预览长度 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              预览长度
              <span class="text-xs text-gray-500">(字符数, 0=完全锁定)</span>
            </label>
            <input v-model.number="form.previewLength" type="number" min="0" placeholder="80" :disabled="form.isFree"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed" />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {{ form.isFree ? '完全免费时无需设置预览长度' : '建议: 80字符 ≈ 10-15个单词' }}
            </p>
          </div>
        </div>
      </div>

      <!-- 包含话题（仅编辑模式显示） -->
      <div v-if="isEditMode" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
            包含话题 <span class="text-sm font-normal text-gray-500">({{ members.length }} 题)</span>
          </h2>
          <button @click="showTopicSelector = true"
            class="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors">
            <PlusIcon class="w-4 h-4" />
            添加话题
          </button>
        </div>
        <!-- 成员列表 -->
        <div v-if="members.length > 0" class="space-y-2">
          <div v-for="(member, index) in members" :key="member.id"
            class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <span class="text-gray-400 dark:text-gray-500 w-6">{{ index + 1 }}.</span>
            <span class="flex-1 text-gray-900 dark:text-gray-100">{{ member.topicName || `话题 #${member.topicId}`
              }}</span>
            <button v-if="member.note" class="text-sm text-green-600 dark:text-green-400">
              已定制
            </button>
            <button @click="handleEditNote(member)"
              class="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
              {{ member.note ? '编辑定制' : '添加定制' }}
            </button>
            <button @click="handleRemoveMember(member)" class="text-sm text-red-600 dark:text-red-400 hover:underline">
              移除
            </button>
          </div>
        </div>
        <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
          暂无话题，点击"添加话题"按钮添加
        </div>
      </div>

      <!-- 串题答案（仅编辑模式显示） - 分屏布局 -->
      <div v-if="isEditMode" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <!-- 标题栏 -->
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">串题答案</h2>
          <div class="flex items-center gap-3">
            <span v-if="answer" class="text-sm text-green-600 dark:text-green-400">✅ 已添加</span>
            <span v-else class="text-sm text-gray-500 dark:text-gray-400">❌ 未添加</span>
          </div>
        </div>

        <!-- 分屏布局：左侧编辑器 + 右侧话题参考 -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- 左侧：答案编辑器 -->
          <div class="space-y-4">
            <!-- 编辑/预览 Tab 切换 -->
            <div class="flex border-b border-gray-200 dark:border-gray-600">
              <button @click="answerViewMode = 'edit'" :class="[
                'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                answerViewMode === 'edit'
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              ]">
                <PencilIcon class="w-4 h-4" />
                编辑
              </button>
              <button @click="answerViewMode = 'preview'" :class="[
                'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                answerViewMode === 'preview'
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              ]">
                <EyeIcon class="w-4 h-4" />
                预览
              </button>
            </div>

            <!-- 编辑区域 -->
            <div v-if="answerViewMode === 'edit'">
              <textarea v-model="answerForm.content" rows="16" placeholder="输入串题答案内容（支持 Markdown 格式）..."
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm resize-y"></textarea>
              <!-- Markdown 提示 -->
              <div class="flex flex-wrap gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">**粗体**</span>
                <span class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">*斜体*</span>
                <span class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded"># 标题</span>
                <span class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">- 列表</span>
              </div>
            </div>

            <!-- 预览区域 -->
            <div v-else
              class="min-h-[400px] max-h-[500px] overflow-y-auto px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700">
              <div v-html="renderedAnswerMarkdown"
                class="prose prose-sm dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-gray-100">
              </div>
            </div>

            <!-- 音频链接 -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">音频链接（可选）</label>
              <input v-model="answerForm.audioUrl" type="text" placeholder="https://..."
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
            </div>

            <!-- 保存按钮 -->
            <div class="flex gap-2">
              <button @click="handleSaveAnswer" :disabled="savingAnswer || !isAnswerDirty"
                class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {{ savingAnswer ? '保存中...' : (answer ? '更新答案' : '添加答案') }}
              </button>
              <button v-if="answer" @click="handleDeleteAnswer"
                class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors">
                删除答案
              </button>
            </div>
          </div>

          <!-- 右侧：话题参考面板 -->
          <div class="lg:border-l lg:border-gray-200 dark:lg:border-gray-600 lg:pl-6">
            <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              📋 话题参考
              <span class="font-normal text-gray-500">({{ members.length }} 个话题)</span>
            </h3>

            <!-- 话题列表（可滚动） -->
            <div v-if="members.length > 0" class="space-y-4 max-h-[500px] overflow-y-auto pr-2">
              <div v-for="(member, index) in members" :key="member.id"
                class="pb-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                <!-- 话题名称 -->
                <div class="flex items-start gap-2">
                  <span
                    class="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300 text-xs font-medium rounded">
                    {{ index + 1 }}
                  </span>
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-gray-900 dark:text-gray-100 text-sm">
                      {{ member.topicName || `话题 #${member.topicId}` }}
                    </div>
                    <!-- Part 2 问题内容 -->
                    <div v-if="member.part2Question"
                      class="mt-1.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {{ member.part2Question }}
                    </div>
                    <div v-else class="mt-1 text-sm text-gray-400 dark:text-gray-500 italic">
                      暂无 Part 2 问题
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
              <p class="text-sm">暂无话题</p>
              <p class="text-xs mt-1">请先在上方添加话题</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 话题选择器弹窗 -->
    <TopicSelectorModal v-if="showTopicSelector" :existing-topic-ids="existingTopicIds"
      @close="showTopicSelector = false" @select="handleAddMembers" />

    <!-- 话题定制弹窗 -->
    <MemberNoteModal v-if="editingMember" :member="editingMember" @close="editingMember = null"
      @save="handleSaveNote" />

    <!-- 确认弹窗 -->
    <ConfirmModal ref="confirmModalRef" />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'
import { ArrowLeftIcon, PlusIcon, PencilIcon, EyeIcon } from '@heroicons/vue/24/outline'
import MarkdownIt from 'markdown-it'
import {
  getTopicGroupAPI,
  createTopicGroupAPI,
  updateTopicGroupAPI,
  addMembersBatchAPI,
  removeMemberAPI,
  createAnswerAPI,
  updateAnswerAPI,
  deleteAnswerAPI,
  createNoteAPI,
  updateNoteAPI,
} from '@/api/speaking/topicGroup'
import { fetchCategoriesAPI } from '@/api/speaking/topic'
import type { TopicGroupMember, TopicGroupAnswer } from '@/types/topic/topicGroup'
import type { Category } from '@/types/topic/topic'
import toaster from '@/utils/toaster'
import ConfirmModal from '@/components/ui/ConfirmModal.vue'
import TopicSelectorModal from '@/components/speaking/TopicSelectorModal.vue'
import MemberNoteModal from '@/components/speaking/MemberNoteModal.vue'

const router = useRouter()
const route = useRoute()

// 状态
const loading = ref(false)
const saving = ref(false)
const savingAnswer = ref(false)
const categories = ref<Category[]>([])
const members = ref<TopicGroupMember[]>([])
const answer = ref<TopicGroupAnswer | null>(null)
const confirmModalRef = ref<InstanceType<typeof ConfirmModal> | null>(null)
const showTopicSelector = ref(false)
const editingMember = ref<TopicGroupMember | null>(null)

// Markdown 渲染器
const md = new MarkdownIt()

// 答案编辑视图模式：编辑 or 预览
const answerViewMode = ref<'edit' | 'preview'>('edit')

/**
 * 渲染后的答案 Markdown 内容
 */
const renderedAnswerMarkdown = computed(() => {
  if (!answerForm.content) return '<p class="text-gray-400">暂无内容</p>'
  return md.render(answerForm.content)
})

// 原始数据快照（用于检测是否有未保存的修改）
const originalBasicInfo = ref({
  name: '',
  categoryId: undefined as number | undefined,
  orderId: 0,
  isVisible: true,
  isFree: false,
  previewLength: 80
})
const originalAnswerContent = ref('')

// 计算属性
const isEditMode = computed(() => !!route.params.id)
const groupId = computed(() => Number(route.params.id) || 0)
const existingTopicIds = computed(() => members.value.map(m => m.topicId))

/**
 * 检测基本信息是否有未保存的修改
 */
const isBasicInfoDirty = computed(() => {
  if (!isEditMode.value) return false
  return (
    form.name !== originalBasicInfo.value.name ||
    form.categoryId !== originalBasicInfo.value.categoryId ||
    form.orderId !== originalBasicInfo.value.orderId ||
    form.isVisible !== originalBasicInfo.value.isVisible ||
    form.isFree !== originalBasicInfo.value.isFree ||
    form.previewLength !== originalBasicInfo.value.previewLength
  )
})

/**
 * 检测答案是否有未保存的修改
 */
const isAnswerDirty = computed(() => {
  if (!isEditMode.value) return false
  return answerForm.content !== originalAnswerContent.value
})

/**
 * 检测是否有任何未保存的修改
 */
const hasUnsavedChanges = computed(() => {
  return isBasicInfoDirty.value || isAnswerDirty.value
})

// 表单
const form = reactive({
  name: '',
  categoryId: undefined as number | undefined,
  orderId: 0,
  isVisible: true,
  isFree: false,
  previewLength: 80,
})

const answerForm = reactive({
  content: '',
  audioUrl: '' as string | null,
})

/**
 * 更新原始数据快照（保存成功后调用）
 */
const updateOriginalBasicInfo = () => {
  originalBasicInfo.value = {
    name: form.name,
    categoryId: form.categoryId,
    orderId: form.orderId,
    isVisible: form.isVisible,
    isFree: form.isFree,
    previewLength: form.previewLength,
  }
}

const updateOriginalAnswerContent = () => {
  originalAnswerContent.value = answerForm.content
}

// ==================== 离开提示 ====================

/**
 * 浏览器关闭/刷新时的提示
 */
const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  if (hasUnsavedChanges.value) {
    e.preventDefault()
    e.returnValue = ''
    return ''
  }
}

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

/**
 * Vue Router 离开页面时的提示
 */
onBeforeRouteLeave(async (_to, _from, next) => {
  if (hasUnsavedChanges.value) {
    const confirmed = await confirmModalRef.value?.confirm(
      '您有未保存的更改，离开后将丢失。',
      '确定要离开吗？'
    )
    if (confirmed) {
      next()
    } else {
      next(false)
    }
  } else {
    next()
  }
})

/**
 * 加载分类列表
 */
const loadCategories = async () => {
  try {
    const response = await fetchCategoriesAPI()
    if (response.data) {
      categories.value = response.data
    }
  } catch (error) {
    console.error('加载分类失败:', error)
  }
}

/**
 * 加载串题组详情
 */
const loadTopicGroup = async () => {
  if (!isEditMode.value) return

  loading.value = true
  try {
    const response = await getTopicGroupAPI(groupId.value)
    if (response.data) {
      const group = response.data
      form.name = group.name
      form.categoryId = group.categoryId
      form.orderId = group.orderId
      form.isVisible = group.isVisible
      form.isFree = group.isFree
      form.previewLength = group.previewLength
      members.value = group.members || []
      answer.value = group.answer || null

      if (group.answer) {
        answerForm.content = group.answer.content
        answerForm.audioUrl = group.answer.audioUrl
      }

      // 更新原始数据快照（用于检测未保存的修改）
      updateOriginalBasicInfo()
      updateOriginalAnswerContent()
    }
  } catch (error) {
    toaster.error('加载串题组失败')
    console.error('加载串题组失败:', error)
    router.push('/topic-group')
  } finally {
    loading.value = false
  }
}

/**
 * 返回列表页
 */
const handleBack = () => {
  router.push('/topic-group')
}

/**
 * 保存串题组（创建模式：创建新串题组）
 */
const handleSave = async () => {
  if (!form.name.trim()) {
    toaster.error('请输入组名')
    return
  }
  if (!form.categoryId) {
    toaster.error('请选择分类')
    return
  }

  saving.value = true
  try {
    const response = await createTopicGroupAPI({
      name: form.name,
      categoryId: form.categoryId,
      orderId: form.orderId,
      isVisible: form.isVisible,
      isFree: form.isFree,
      previewLength: form.previewLength,
    })
    toaster.success('创建成功')
    // 创建成功后跳转到编辑页
    if (response.data?.id) {
      router.replace(`/topic-group/${response.data.id}/edit`)
    }
  } catch (error) {
    toaster.error('创建失败')
    console.error('保存串题组失败:', error)
  } finally {
    saving.value = false
  }
}

/**
 * 保存基本信息（编辑模式：更新基本信息）
 */
const handleSaveBasicInfo = async () => {
  if (!form.name.trim()) {
    toaster.error('请输入组名')
    return
  }
  if (!form.categoryId) {
    toaster.error('请选择分类')
    return
  }

  saving.value = true
  try {
    await updateTopicGroupAPI(groupId.value, {
      name: form.name,
      categoryId: form.categoryId,
      orderId: form.orderId,
      isVisible: form.isVisible,
      isFree: form.isFree,
      previewLength: form.previewLength,
    })
    toaster.success('基本信息已保存')
    // 更新原始快照，清除 dirty 状态
    updateOriginalBasicInfo()
  } catch (error) {
    toaster.error('保存失败')
    console.error('保存基本信息失败:', error)
  } finally {
    saving.value = false
  }
}

/**
 * 添加话题成员
 */
const handleAddMembers = async (topicIds: string[]) => {
  if (topicIds.length === 0) return

  try {
    await addMembersBatchAPI(groupId.value, { topicIds })
    toaster.success(`成功添加 ${topicIds.length} 个话题`)
    showTopicSelector.value = false
    await loadTopicGroup()
  } catch (error) {
    toaster.error('添加话题失败')
    console.error('添加话题失败:', error)
  }
}

/**
 * 移除话题成员
 */
const handleRemoveMember = async (member: TopicGroupMember) => {
  if (!confirmModalRef.value) return

  const confirmed = await confirmModalRef.value.confirm(
    `确定要移除话题"${member.topicName || member.topicId}"吗？`,
    '确认移除'
  )
  if (!confirmed) return

  try {
    await removeMemberAPI(groupId.value, member.id)
    toaster.success('移除成功')
    members.value = members.value.filter(m => m.id !== member.id)
  } catch (error) {
    toaster.error('移除失败')
    console.error('移除话题失败:', error)
  }
}

/**
 * 编辑话题定制
 */
const handleEditNote = (member: TopicGroupMember) => {
  editingMember.value = member
}

/**
 * 保存话题定制
 */
const handleSaveNote = async (data: { intro?: string; outro?: string; notes?: string }) => {
  if (!editingMember.value) return

  try {
    const memberId = editingMember.value.id
    if (editingMember.value.note) {
      await updateNoteAPI(memberId, data)
    } else {
      await createNoteAPI(memberId, data)
    }
    toaster.success('保存成功')
    editingMember.value = null
    await loadTopicGroup()
  } catch (error) {
    toaster.error('保存失败')
    console.error('保存话题定制失败:', error)
  }
}

/**
 * 保存答案
 */
const handleSaveAnswer = async () => {
  if (!answerForm.content.trim()) {
    toaster.error('请输入答案内容')
    return
  }

  savingAnswer.value = true
  try {
    const data = {
      content: answerForm.content,
      audioUrl: answerForm.audioUrl || undefined,
    }

    if (answer.value) {
      await updateAnswerAPI(groupId.value, data)
    } else {
      await createAnswerAPI(groupId.value, data)
    }
    toaster.success('答案保存成功')
    await loadTopicGroup()
  } catch (error) {
    toaster.error('保存答案失败')
    console.error('保存答案失败:', error)
  } finally {
    savingAnswer.value = false
  }
}

/**
 * 删除答案
 */
const handleDeleteAnswer = async () => {
  if (!confirmModalRef.value) return

  const confirmed = await confirmModalRef.value.confirm(
    '确定要删除答案吗？<br><br><span style="color: #ef4444;">此操作不可恢复</span>',
    '确认删除'
  )
  if (!confirmed) return

  try {
    await deleteAnswerAPI(groupId.value)
    toaster.success('删除成功')
    answer.value = null
    answerForm.content = ''
    answerForm.audioUrl = ''
  } catch (error) {
    toaster.error('删除失败')
    console.error('删除答案失败:', error)
  }
}

// 组件挂载时加载数据
onMounted(async () => {
  await loadCategories()
  if (isEditMode.value) {
    await loadTopicGroup()
  }
})
</script>
